/**
 * Cliente del **portal del consumidor** (#46). Es el tercer portal de la
 * plataforma y no habla el mismo idioma que los otros dos.
 *
 * Por qué no usa `ApiClient` (que es el que usa todo el resto)
 * -----------------------------------------------------------
 * `/consumer/portal/*` no está bajo `/api/v1` y **no lleva el JWT del
 * dashboard**: su sesión es la del comprador —un token opaco que devuelve
 * `POST /consumer/portal/login`— y va en su propio `Authorization`. El
 * cliente del dashboard sólo sabe mandar el bearer del tenant o ninguno
 * (`auth: false`); no tiene forma de mandar *otro*. Mandarle el JWT del
 * dashboard a un endpoint público, además, es exactamente lo que no se
 * quiere: son dos identidades distintas y no deben cruzarse nunca.
 *
 * Lo que sí se reutiliza es el manejo de errores (`apiErrorFromResponse`), así
 * que un fallo de acá se muestra con el mismo `ErrorState` y el mismo
 * `trace_id` que cualquier otro, y `encodeQuery`, para no tener dos formas de
 * armar un query string en el mismo bundle.
 *
 * Nada de esto es `useEffect` + `fetch`: las llamadas viven adentro de un
 * `queryFn` / `mutationFn` de TanStack Query, con su cache y su
 * deduplicación, igual que el resto del dashboard.
 */
import { apiErrorFromResponse, networkError } from '@/api/errors';
import { encodeQuery } from '@/api/client';
import type { components } from '@/api/schema';
import type { Page, QueryParams } from '@/api/types';

export type PortalConfig = components['schemas']['PortalConfigOut'];
export type PortalSession = components['schemas']['PortalSessionOut'];
export type PortalAccount = components['schemas']['PortalAccountOut'];
export type SavedCode = components['schemas']['SavedCodeOut'];
export type PortalDataExport = components['schemas']['services__api__routers__consumer__ExportOut'];
export type RegisterBody = components['schemas']['RegisterIn'];
export type PortalLoginBody = components['schemas']['PortalLoginIn'];
export type SaveCodeBody = components['schemas']['SaveCodeIn'];

/**
 * Versión del texto de privacidad que la persona acepta al registrarse. Se
 * guarda junto al consentimiento: sin ella no se puede demostrar **a qué**
 * dio su consentimiento, que es la mitad del requisito.
 */
export const NOTICE_VERSION = 'consumer-2026-07';

/** Cuánto espera una llamada del portal antes de cortar. */
const TIMEOUT_MS = 15_000;

export interface PortalRequestOptions {
  method?: 'GET' | 'POST' | 'DELETE';
  body?: unknown;
  /** Token de la sesión del comprador. Ausente en config, login y alta. */
  token?: string | null;
  query?: QueryParams;
  signal?: AbortSignal;
}

export async function portalRequest<T>(
  path: string,
  options: PortalRequestOptions = {},
): Promise<T> {
  const headers = new Headers({ Accept: 'application/json' });
  if (options.token !== undefined && options.token !== null) {
    headers.set('Authorization', `Bearer ${options.token}`);
  }

  let payload: string | undefined;
  if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json');
    payload = JSON.stringify(options.body);
  }

  const timeout = AbortSignal.timeout(TIMEOUT_MS);
  const signal =
    options.signal === undefined ? timeout : AbortSignal.any([options.signal, timeout]);

  let response: Response;
  try {
    response = await fetch(path + encodeQuery(options.query), {
      method: options.method ?? 'GET',
      headers,
      ...(payload === undefined ? {} : { body: payload }),
      signal,
      // El portal tampoco usa cookies: la sesión viaja en el header.
      credentials: 'omit',
      redirect: 'error',
    });
  } catch (cause) {
    throw networkError(cause);
  }

  if (!response.ok) throw await apiErrorFromResponse(response);
  // 204: logout, borrar un código, borrar la cuenta. Nadie lee el valor.
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

// ───────────────────────────────────────────────────────── operaciones

export function portalLogin(body: PortalLoginBody): Promise<PortalSession> {
  return portalRequest<PortalSession>('/consumer/portal/login', { method: 'POST', body });
}

export function portalRegister(body: RegisterBody): Promise<PortalSession> {
  return portalRequest<PortalSession>('/consumer/portal/register', { method: 'POST', body });
}

export async function portalLogout(token: string): Promise<void> {
  await portalRequest<unknown>('/consumer/portal/logout', { method: 'POST', token });
}

export function portalMe(token: string, signal?: AbortSignal): Promise<PortalAccount> {
  return portalRequest<PortalAccount>('/consumer/portal/me', {
    token,
    ...(signal === undefined ? {} : { signal }),
  });
}

export function portalCodes(
  token: string,
  query: QueryParams,
  signal?: AbortSignal,
): Promise<Page<SavedCode>> {
  return portalRequest<Page<SavedCode>>('/consumer/portal/codes', {
    token,
    query,
    ...(signal === undefined ? {} : { signal }),
  });
}

export function portalSaveCode(token: string, body: SaveCodeBody): Promise<SavedCode> {
  return portalRequest<SavedCode>('/consumer/portal/codes', { method: 'POST', token, body });
}

export async function portalDeleteCode(token: string, ref: string): Promise<void> {
  await portalRequest<unknown>(`/consumer/portal/codes/${encodeURIComponent(ref)}`, {
    method: 'DELETE',
    token,
  });
}

export function portalExportData(token: string): Promise<PortalDataExport> {
  return portalRequest<PortalDataExport>('/consumer/portal/export', { token });
}

export async function portalDeleteAccount(token: string): Promise<void> {
  await portalRequest<unknown>('/consumer/portal/account', { method: 'DELETE', token });
}
